import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useRole } from '../hooks/useRole';
import NotificationBell from './NotificationBell';
import { isOwnerEmail } from '../data/teamMembers';

// Flat blue palette — matches the redesigned pages (NewDocument/Dashboard/etc.).
const C_BG = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#6B7C93';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';

/**
 * THE SIDEBAR'S WIDTH, AND THE ONE PLACE IT IS WRITTEN DOWN.
 *
 * The sidebar is position:fixed, so it takes no space in the flow and every
 * page has to leave a matching gap for it with its own marginLeft. That number
 * was hard-coded as a bare 240 in SIXTEEN places — the sidebar itself plus
 * fifteen pages — with nothing tying them together. Changing the sidebar meant
 * finding all sixteen, and missing one would leave that page either sitting
 * under the nav or floating away from it, on that page alone.
 *
 * Narrowed from 240 to 200 to give the dashboard board back 40px, which is
 * roughly what a seventh column costs. 200 is still a width, not an icon rail:
 * the longest nav label ("Announcements" at 14px) and the logo block ("Fence
 * Flow" at 20px over "ATLANTIC FENCE & SUPPLY" at 10px) both still fit, and
 * nothing was reduced to a glyph.
 *
 * The shop screen has no sidebar and no offset, so none of this reaches it.
 */
export const SIDEBAR_W = 200;

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useRole();

  // displayName: useRole().userName already resolves to email.split('@')[0]
  // uppercased, so we no longer need the `user` prop the page used to pass.
  const { userName: roleName } = useRole();
  const displayName = roleName || auth.currentUser?.email?.split('@')[0]?.toUpperCase();

  // Owner-only nav entry. A UI hide, not a permission — the route itself
  // repeats the check, and neither is security. See Announcements.js.
  const isOwner = isOwnerEmail(auth.currentUser?.email);

  const handleLogout = async () => { await signOut(auth); };

  const NAV = [
    { icon: '🏠', label: 'Dashboard', path: '/dashboard' },
    { icon: '📄', label: 'Quotes', path: '/documents?tab=quotes' },
    { icon: '🧾', label: 'Invoices', path: '/documents?tab=invoices' },
    { icon: '👥', label: 'Customers', path: '/customers' },
    { icon: '💲', label: 'Price List', path: '/pricelist' },
    { icon: '⚡', label: 'Quick Prices', path: '/quick-prices' },
    { icon: '📊', label: 'Reports', path: '/reports' },
    { icon: '🔨', label: 'Nail Calculator', path: '/nail-calculator' },
    { icon: '📐', label: 'Shop Drawings', path: '/shop-drawings' },
    ...(isOwner ? [{ icon: '📣', label: 'Announcements', path: '/announcements' }] : []),
    ...(isAdmin ? [
      { icon: '📦', label: 'Inventory', path: '/inventory' },
      { icon: '📋', label: 'Templates', path: '/templates' },
      { icon: '⚙️', label: 'Settings', path: '/settings' },
    ] : [
      { icon: '📦', label: 'Inventory', adminOnly: true },
      { icon: '📋', label: 'Templates', path: '/templates' },
      { icon: '⚙️', label: 'Settings', adminOnly: true },
    ]),
  ];

  // ── Sidebar nav item renderer ──
  const renderNavItem = (item) => {
    if (item.disabled) {
      return (
        <div
          key={item.label}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 12px', borderRadius: 10,
            color: C_TEXT_3, opacity: 0.5, cursor: 'not-allowed', userSelect: 'none',
            fontSize: 14,
          }}
        >
          <span style={{ fontSize: 16 }}>{item.icon}</span>
          <span>{item.label}</span>
          <span style={{
            marginLeft: 'auto', fontSize: 10, padding: '2px 6px', borderRadius: 4,
            background: C_TINT, color: C_TEXT_3,
          }}>Soon</span>
        </div>
      );
    }
    if (item.adminOnly) {
      return (
        <div
          key={item.label}
          title="Admin only"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 12px', borderRadius: 10,
            color: C_TEXT_3, opacity: 0.5, cursor: 'not-allowed', userSelect: 'none',
            fontSize: 14,
          }}
        >
          <span style={{ fontSize: 16 }}>{item.icon}</span>
          <span>{item.label}</span>
          <span style={{
            marginLeft: 'auto', fontSize: 10, padding: '2px 6px', borderRadius: 4,
            background: C_TINT, color: C_TEXT_3,
          }}>Admin</span>
        </div>
      );
    }
    // Items may carry a ?tab= query (Quotes/Invoices share the /documents route),
    // so compare pathname AND tab. Non-query items keep the plain pathname check.
    const [itemPath, itemQuery] = item.path.split('?');
    const currentTab = new URLSearchParams(location.search).get('tab') || 'quotes';
    const itemTab = itemQuery ? new URLSearchParams(itemQuery).get('tab') : null;
    const active = itemTab
      ? (location.pathname === itemPath && currentTab === itemTab)
      : (location.pathname === itemPath);
    return (
      <button
        key={item.label}
        onClick={() => navigate(item.path)}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 12px',
          paddingLeft: active ? 9 : 12, // compensate for the left border
          borderRadius: 10,
          border: 'none',
          borderLeft: active ? `3px solid ${C_ACCENT}` : '3px solid transparent',
          background: active ? C_TINT : 'transparent',
          color: active ? C_ACCENT : C_TEXT_2,
          fontSize: 14, fontWeight: active ? 600 : 500,
          textAlign: 'left', cursor: 'pointer',
          transition: 'background 0.2s ease, color 0.2s ease',
        }}
        onMouseEnter={e => {
          if (!active) {
            e.currentTarget.style.background = 'rgba(0, 119, 182, 0.06)';
            e.currentTarget.style.color = C_TEXT;
          }
        }}
        onMouseLeave={e => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = C_TEXT_2;
          }
        }}
      >
        <span style={{ fontSize: 16 }}>{item.icon}</span>
        <span>{item.label}</span>
      </button>
    );
  };

  return (
    <aside style={{
      position: 'fixed', top: 0, left: 0, height: '100%', width: SIDEBAR_W,
      display: 'flex', flexDirection: 'column', zIndex: 20,
      background: C_BG,
      borderRight: `1px solid ${C_BORDER}`,
      transition: 'background 0.3s ease, border-color 0.3s ease',
    }}>
      {/* Sidebar scrollbar styling (formerly provided by Dashboard's .dash-scroll) */}
      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #C3CBD6; border-radius: 3px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #A9B4C2; }
      `}</style>

      {/* Logo */}
      <div style={{ padding: '20px', borderBottom: `1px solid ${C_BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: C_TINT,
            border: `1px solid ${C_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <img
              src={require('../assets/fenceflow-logo.png')}
              alt="Fence Flow"
              style={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: '800',
              margin: 0,
              color: C_TEXT,
              letterSpacing: '-0.02em',
            }}>
              Fence Flow
            </h1>
            <p style={{
              fontSize: '10px',
              color: C_TEXT_2,
              margin: 0,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontWeight: '500',
            }}>
              Atlantic Fence & Supply
            </p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="sidebar-scroll" style={{
        flex: 1, padding: '16px 12px',
        display: 'flex', flexDirection: 'column', gap: 2,
        overflowY: 'auto',
      }}>
        {NAV.map(renderNavItem)}
      </nav>

      {/* Footer — user info */}
      <div style={{ padding: '16px 12px', borderTop: `1px solid ${C_BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 4 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C_ACCENT, fontWeight: 700, fontSize: 13,
            background: C_TINT,
            border: `1px solid ${C_BORDER}`,
            flexShrink: 0,
          }}>
            {displayName?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: C_TEXT, fontSize: 13, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </p>
            <span style={{
              display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              marginTop: 2,
              background: isAdmin ? C_TINT : '#CDE7F5',
              color: isAdmin ? C_ACCENT : C_TEXT_2,
              border: `1px solid ${C_BORDER}`,
            }}>
              {isAdmin ? 'ADMIN' : 'SALESPERSON'}
            </span>
          </div>

          {/* The bell lives HERE, not in a page header: every page renders its
              own header — fifteen of them — but they all render this sidebar,
              so this is the one spot that is genuinely present everywhere.
              With nothing unread it draws no badge and the block reads exactly
              as it did before. */}
          <NotificationBell displayName={displayName} />
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 12px', borderRadius: 10,
            background: 'transparent', border: 'none',
            color: C_TEXT_2, fontSize: 14, cursor: 'pointer',
            transition: 'background 0.2s ease, color 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#FBECEC';
            e.currentTarget.style.color = '#A32D2D';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = C_TEXT_2;
          }}
        >
          <span style={{ fontSize: 16 }}>🚪</span>
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
